import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Bell, Check, Trash, Gear, GameController, Users, Trophy,
  Storefront, Megaphone, Star, Robot, BookOpen, ShieldCheck, Lightning,
  ChatCircleDots, Handshake, Crown, Medal, Eye, Funnel
} from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  session_updates: { label: 'Sessions', icon: GameController, color: '#00F0FF' },
  messages: { label: 'Messages', icon: ChatCircleDots, color: '#9D4CDD' },
  open_seat_alerts: { label: 'Open Seats', icon: Users, color: '#39FF14' },
  tournament_updates: { label: 'Tournaments', icon: Trophy, color: '#FFD700' },
  shop_events: { label: 'Shop Events', icon: Storefront, color: '#F97316' },
  favorite_games: { label: 'Favorites', icon: Star, color: '#EF4444' },
  discovery_recommendations: { label: 'Discovery', icon: Eye, color: '#3B82F6' },
  rivalry_prompts: { label: 'Rivalries', icon: Handshake, color: '#F59E0B' },
  achievement_updates: { label: 'Achievements', icon: Medal, color: '#10B981' },
  reminder_alerts: { label: 'Reminders', icon: Bell, color: '#F97316' },
  moderation_or_system: { label: 'System', icon: ShieldCheck, color: '#888' },
  publisher_content: { label: 'Publisher', icon: BookOpen, color: '#3B82F6' },
  premium_or_plan: { label: 'Premium', icon: Crown, color: '#9D4CDD' },
  admin_internal: { label: 'Admin', icon: Lightning, color: '#EF4444' },
  // Legacy types
  friend_request: { label: 'Friends', icon: Users, color: '#00F0FF' },
  friend_accepted: { label: 'Friends', icon: Handshake, color: '#10B981' },
  group_invite: { label: 'Groups', icon: Users, color: '#9D4CDD' },
  group_joined: { label: 'Groups', icon: Users, color: '#39FF14' },
  group_message: { label: 'Messages', icon: ChatCircleDots, color: '#F97316' },
  tournament: { label: 'Tournaments', icon: Trophy, color: '#FFD700' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (activeFilter !== 'all') params.set('category', activeFilter);
    if (showUnreadOnly) params.set('unread_only', 'true');
    axios.get(`${API_URL}/api/notifications?${params}`, { withCredentials: true })
      .then(({ data }) => setNotifications(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeFilter, showUnreadOnly]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  const markAllRead = async () => {
    await axios.put(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const dismiss = async (id) => {
    await axios.delete(`${API_URL}/api/notifications/${id}`, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const filterCategories = ['all', 'session_updates', 'messages', 'tournament_updates', 'shop_events', 'achievement_updates', 'friend_request'];

  return (
    <div className="min-h-screen bg-[#0A0A12] pb-24" data-testid="notifications-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0A0A12]/95 backdrop-blur-lg border-b border-white/5">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-white"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-white font-bold text-lg" style={{ fontFamily: 'Unbounded' }}>Notifications</h1>
              {unreadCount > 0 && <p className="text-[10px] text-[#F59E0B]">{unreadCount} unread</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-[11px] text-[#00F0FF] px-2 py-1 rounded-lg hover:bg-white/5" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
            <button onClick={() => navigate('/notifications/settings')} className="p-2 rounded-xl hover:bg-white/5" data-testid="notification-settings-btn">
              <Gear size={18} className="text-[#555]" />
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-none">
          {filterCategories.map(cat => {
            const meta = CATEGORY_META[cat];
            const isActive = activeFilter === cat;
            return (
              <button key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all flex items-center gap-1.5 ${isActive ? 'text-white' : 'text-[#555] hover:text-[#888]'}`}
                style={isActive ? { backgroundColor: (meta?.color || '#888') + '20', color: meta?.color || '#fff' } : { backgroundColor: 'rgba(255,255,255,0.03)' }}
                data-testid={`filter-${cat}`}
              >
                {cat === 'all' ? <Funnel size={12} /> : meta && React.createElement(meta.icon, { size: 12 })}
                {cat === 'all' ? 'All' : meta?.label || cat}
              </button>
            );
          })}
          <button
            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${showUnreadOnly ? 'bg-[#EF4444]/15 text-[#EF4444]' : 'bg-white/[0.03] text-[#555]'}`}
            data-testid="unread-filter"
          >
            {showUnreadOnly ? 'Unread' : 'All status'}
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="px-4 py-3 space-y-1">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-white/[0.02] rounded-xl animate-pulse mb-2" />
          ))
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell size={40} weight="duotone" className="text-[#333] mx-auto mb-3" />
            <p className="text-[#555] text-sm">No notifications yet</p>
            <p className="text-[#333] text-xs mt-1">
              {showUnreadOnly ? 'All caught up!' : 'Notifications will appear here when you get activity'}
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {notifications.map((n, i) => {
              const cat = n.category || n.notification_type || n.type;
              const meta = CATEGORY_META[cat] || { label: cat, icon: Bell, color: '#888' };
              const Icon = meta.icon;
              const accent = meta.color;
              const isPriority = n.priority === 'high' || n.priority === 'urgent';

              return (
                <motion.div
                  key={n.id}
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }} transition={{ delay: i * 0.02 }}
                  className={`rounded-xl border p-3.5 flex items-start gap-3 transition-all group ${!n.is_read ? 'bg-white/[0.02]' : 'bg-transparent'}`}
                  style={{ borderColor: !n.is_read ? `${accent}15` : 'rgba(255,255,255,0.03)' }}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${accent}12` }}>
                    <Icon size={16} weight="duotone" style={{ color: accent }} />
                  </div>

                  <div className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => { markRead(n.id); if (n.action_url || n.link) navigate(n.action_url || n.link); }}>
                    <div className="flex items-center gap-2">
                      <p className={`text-[13px] font-medium truncate ${n.is_read ? 'text-[#888]' : 'text-white'}`}>{n.title}</p>
                      {isPriority && <span className="text-[8px] px-1.5 py-0.5 rounded bg-[#EF4444]/10 text-[#EF4444] font-bold uppercase">urgent</span>}
                      {!n.is_read && <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />}
                    </div>
                    <p className={`text-[11px] mt-0.5 line-clamp-2 ${n.is_read ? 'text-[#444]' : 'text-[#777]'}`}>{n.body}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${accent}10`, color: accent }}>{meta.label}</span>
                      <span className="text-[10px] text-[#333]">{timeAgo(n.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!n.is_read && (
                      <button onClick={() => markRead(n.id)} className="p-1 rounded hover:bg-white/5 text-[#555] hover:text-[#10B981]" title="Mark read">
                        <Check size={12} />
                      </button>
                    )}
                    <button onClick={() => dismiss(n.id)} className="p-1 rounded hover:bg-white/5 text-[#555] hover:text-[#EF4444]" title="Dismiss">
                      <Trash size={12} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
